import { useEffect, useRef } from 'react';

export function loadDraft<T>(key: string): T | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch { return null; }
}

export function saveDraft<T>(key: string, value: T) {
  try { localStorage.setItem(key, JSON.stringify(value)); } catch { /* ignore */ }
}

export function clearDraft(key: string) {
  try { localStorage.removeItem(key); } catch { /* ignore */ }
}

/** Debounced autosave of `value` under `key`. Skips the first run if the value matches the just-loaded draft. */
export function useAutosaveDraft<T>(key: string, value: T, isEmpty: (v: T) => boolean, delay = 800) {
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      if (isEmpty(value)) {
        clearDraft(key);
      } else {
        saveDraft(key, value);
      }
    }, delay);
    return () => { if (timer.current) clearTimeout(timer.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, value]);
}
